/**
 * Error Handler Utility
 *
 * Handles quota exceeded, rate limit, and anonymous limit errors
 */

import type { AxiosError } from 'axios';
import type { QuotaExceededError, RateLimitError, AnonymousLimitError } from '../types';

type ApiErrorPayload = Partial<QuotaExceededError & RateLimitError & AnonymousLimitError> & {
  error?: string;
  errorCode?: string;
  message?: string;
};

export interface ErrorHandlerResult {
  isQuotaError: boolean;
  isRateLimitError: boolean;
  isAnonymousLimitError: boolean;
  message: string;
  upgradeUrl?: string;
  retryAfter?: number;
  sessionsUsed?: number;
  maxSessions?: number;
}

/**
 * Check if error is a quota exceeded error
 */
export function isQuotaExceededError(error: unknown): error is AxiosError<QuotaExceededError> {
  const axiosError = error as AxiosError<ApiErrorPayload>;
  const data = axiosError.response?.data;
  return (
    (axiosError?.response?.status === 402 || axiosError?.response?.status === 429) &&
    (data?.errorCode === 'QUOTA_EXCEEDED' ||
      data?.errorCode === 'TOKEN_QUOTA_EXCEEDED' ||
      data?.error === 'Quota exceeded' ||
      data?.error === 'QUOTA_EXCEEDED')
  );
}

/**
 * Check if error is a rate limit error
 */
export function isRateLimitError(error: unknown): error is AxiosError<RateLimitError> {
  const axiosError = error as AxiosError<ApiErrorPayload>;
  const data = axiosError.response?.data;
  return (
    axiosError?.response?.status === 429 &&
    (data?.errorCode === 'RATE_LIMIT_EXCEEDED' || data?.error === 'Rate limit exceeded')
  );
}

/**
 * Check if error is an anonymous limit error
 */
export function isAnonymousLimitError(error: unknown): error is AxiosError<AnonymousLimitError> {
  const axiosError = error as AxiosError<ApiErrorPayload>;
  const data = axiosError.response?.data;
  return (
    axiosError?.response?.status === 429 &&
    (data?.errorCode === 'ANONYMOUS_LIMIT_EXCEEDED' ||
      data?.error === 'ANONYMOUS_LIMIT_EXCEEDED' ||
      data?.error === 'Anonymous session limit exceeded' ||
      data?.error === 'Anonymous message limit exceeded')
  );
}

/**
 * Handle API errors and return structured error info
 */
export function handleApiError(error: unknown): ErrorHandlerResult {
  const defaultResult: ErrorHandlerResult = {
    isQuotaError: false,
    isRateLimitError: false,
    isAnonymousLimitError: false,
    message: 'An unexpected error occurred. Please try again.',
  };

  if (!error) return defaultResult;

  // Handle quota exceeded
  if (isQuotaExceededError(error)) {
    const data = error.response!.data;
    return {
      isQuotaError: true,
      isRateLimitError: false,
      isAnonymousLimitError: false,
      message: data.message,
      upgradeUrl: data.upgradeUrl,
    };
  }

  // Handle rate limit
  if (isRateLimitError(error)) {
    const data = error.response!.data;
    return {
      isQuotaError: false,
      isRateLimitError: true,
      isAnonymousLimitError: false,
      message: data.message,
      retryAfter: data.retryAfter,
    };
  }

  // Handle anonymous limit
  if (isAnonymousLimitError(error)) {
    const data = error.response!.data;
    return {
      isQuotaError: false,
      isRateLimitError: false,
      isAnonymousLimitError: true,
      message: data.message,
      upgradeUrl: data.upgradeUrl,
      sessionsUsed: data.sessionsUsed,
      maxSessions: data.maxSessions,
    };
  }

  // Handle other axios errors
  const axiosError = error as AxiosError<ApiErrorPayload>;
  if (axiosError.response) {
    return {
      ...defaultResult,
      message:
        axiosError.response.data?.message ||
        `Error: ${axiosError.response.status} ${axiosError.response.statusText}`,
    };
  }

  return defaultResult;
}

/**
 * Format error message for display
 */
export function formatErrorMessage(error: ErrorHandlerResult): string {
  if (error.isQuotaError) {
    return error.message;
  }

  if (error.isRateLimitError) {
    return `${error.message} Retry in ${error.retryAfter} seconds.`;
  }

  if (error.isAnonymousLimitError) {
    return `${error.message} (${error.sessionsUsed}/${error.maxSessions} sessions used)`;
  }

  return error.message;
}
